import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { lastValueFrom } from 'rxjs';
import { environment } from 'environments/environment';
import { ThemeModel } from "@app/shared/models/basic.models";
import Utils from "@app/shared/utils/Utils";

class ThemeDictionary {
  [name: string]: ThemeModel;
}

@Injectable({
  providedIn: 'root'
})
export class ThemeService {

  private static readonly DEFAULT_PRIMARY_COLOUR_HEX: string = "#0437F2";

  private static readonly DEFAULT_THEME = new ThemeModel(ThemeService.DEFAULT_PRIMARY_COLOUR_HEX, ThemeService.DEFAULT_PRIMARY_COLOUR_HEX, "assets/img/logo/default.png");

  private themesDictionary: ThemeDictionary | null = null;
  private theme: ThemeModel = ThemeService.DEFAULT_THEME;

  private themeWrapper = document.querySelector('body');

  constructor(private http: HttpClient) {
    this.setThemeStyles(this.theme);
  }

  getCurrentTheme(): ThemeModel {
    return this.theme;
  }

  getPrimaryColourRGB(): { r: number; g: number; b: number } {
    const primaryRgb = Utils.hexToRgb(this.theme.primaryColour);
    if (!!primaryRgb) {
      return primaryRgb;
    }
    else {
      return { r: 4, g: 56, b: 242 };
    }
  }

  private setThemeStyles(theme: ThemeModel) {
    this.themeWrapper?.style.setProperty('--color-primaryColor', theme.primaryColour);
    const primaryRgb = Utils.hexToRgb(theme.primaryColour);
    if (!!primaryRgb) {
      this.themeWrapper?.style.setProperty('--color-primaryColorRGB', primaryRgb.r  + "," + primaryRgb.g + "," + primaryRgb.b);
    }

    this.themeWrapper?.style.setProperty('--color-secondaryColor', theme.secondaryColour);
    const secondaryRgb = Utils.hexToRgb(theme.secondaryColour);
    if (!!secondaryRgb) {
      this.themeWrapper?.style.setProperty('--color-secondaryColorRGB', secondaryRgb.r  + "," + secondaryRgb.g + "," + secondaryRgb.b);
    }
  }

  async setCurrentTheme(themeName: string) {
    await this.loadThemes();
    this.theme = this.themesDictionary?.[themeName] ?? ThemeService.DEFAULT_THEME;
    this.setThemeStyles(this.theme);
  }

  private async loadThemes(): Promise<{ [ name: string ]: ThemeModel }> {
    if (!!this.themesDictionary) {
      return this.themesDictionary;
    }

    let path = "/assets/themes/clientThemes.json";

    if (environment.localhost) { path = "/assets/themes/clientThemes.json"; }

    try {
      const response = await lastValueFrom(this.http.get<{ [ name: string ]: ThemeModel }>(path));
      this.themesDictionary = response;
      return response;
    }
    catch (error) {
      return { };
    }
  }

  // get the themeTitle from the theme css variables
  getSccVar(name: string): string {
    return getComputedStyle(document.documentElement)
      .getPropertyValue(name)
      .replace(/['"]/g, '')
      .trim();
  }

  getThemeTitle(): string {
    return this.getSccVar('--title') || 'PayStay';
  }
}
